// ── ODDS: every market priced from a probability, same 5 % house edge everywhere ──
// The server sends each game a `model` (home win chance + expected goals / runs / points).
// Everything else (spreads, totals, props, correct score…) is built here from that model
// and from each player's own per-game averages. No flat odds: a flat price is an exploit.

export const EDGE = 0.05;
const clamp = (x, lo, hi) => Math.max(lo, Math.min(hi, x));
const round2 = (x) => Math.round(x * 100) / 100;

// Boost Weekend, two kinds: 'markets' = every market priced here × the boost (BOOST), moneylines not;
// 'moneyline' = only the moneylines (priced on the server) × 2. A parlay is never boosted: it is priced from its
// legs' normal odds (App.js getParlayOdds).
let BOOST = 1;
export const setOddsBoost = (b) => { BOOST = b > 1 ? b : 1; };
export const oddsBoost = () => BOOST;
// the moneyline boost (moneylines come priced from the server: × 2 on a moneyline Boost Weekend) — for the
// crossed-out normal price and the parlay legs
let ML_BOOST = 1;
export const setMoneylineBoost = (b) => { ML_BOOST = b > 1 ? b : 1; };
export const moneylineBoost = () => ML_BOOST;

// decimal odds for a probability (null = can't happen, no bet)
export const price = (p, max = 1000) => {
  if (!(p > 0.0005)) return null;
  return round2(clamp((1 - EDGE) / Math.min(p, 0.99), 1.01, max) * BOOST);
};
// long shots the model is less sure about (exact scores, cycles, first/winning scorer):
// priced a bit shorter so a model miss can't turn into free money
export const priceLong = (p, max = 1000) => {
  if (!(p > 0.0005)) return null;
  return round2(clamp(0.9 / Math.min(p, 0.99), 1.01, max) * BOOST);
};

// ── distributions ──
const poissonPmf = (k, l) => {
  let p = Math.exp(-l);
  for (let i = 1; i <= k; i++) p *= l / i;
  return p;
};
// P(X ≥ k), Poisson
export const poisAtLeast = (k, l) => {
  if (k <= 0) return 1;
  let c = 0;
  for (let i = 0; i < k; i++) c += poissonPmf(i, l);
  return clamp(1 - c, 0, 1);
};
// negative binomial with mean m and variance m·r (r > 1 = streakier than Poisson)
const nbPmfArr = (m, r, max) => {
  if (r <= 1.0001) return Array.from({ length: max + 1 }, (_, k) => poissonPmf(k, m));
  const p = 1 / r, n = m * p / (1 - p);
  const out = [];
  let v = Math.pow(p, n);
  for (let k = 0; k <= max; k++) {
    out.push(v);
    v = v * (k + n) / (k + 1) * (1 - p);
  }
  return out;
};
export const nbAtLeast = (k, m, r) => {
  if (k <= 0) return 1;
  const arr = nbPmfArr(m, r, k);
  return clamp(1 - arr.slice(0, k).reduce((a, b) => a + b, 0), 0, 1);
};
const normCdf = (x) => {
  const t = 1 / (1 + 0.2316419 * Math.abs(x));
  const d = 0.3989423 * Math.exp(-x * x / 2);
  const q = d * t * (0.3193815 + t * (-0.3565638 + t * (1.781478 + t * (-1.821256 + t * 1.330274))));
  return x > 0 ? 1 - q : q;
};
// over/under a .5 line from a list of probabilities (index = value)
const overUnder = (pmf, line) => {
  let under = 0;
  for (let k = 0; k < pmf.length && k < line; k++) under += pmf[k];
  return { over: price(1 - under), under: price(under) };
};

// ═════════════════════════════ NHL ═════════════════════════════
// Final-score grid: Poisson goals per team, late empty-netters (a 1-goal lead becomes 2
// about 20 % of the time), regulation ties more common than Poisson says (≈ 23 %),
// ties decided in OT / shootout (+1 to the winner).
const NHL_MAX = 12;
function nhlGrids(lh, la, otHome) {
  const reg = [];
  let sum = 0;
  for (let i = 0; i <= NHL_MAX; i++) {
    reg.push([]);
    for (let j = 0; j <= NHL_MAX; j++) {
      let v = poissonPmf(i, lh) * poissonPmf(j, la);
      if (i === j) v *= 1.5;
      reg[i].push(v); sum += v;
    }
  }
  for (let i = 0; i <= NHL_MAX; i++) for (let j = 0; j <= NHL_MAX; j++) reg[i][j] /= sum;
  // empty-net goals
  const en = reg.map((r) => r.slice());
  for (let i = 0; i <= NHL_MAX; i++) for (let j = 0; j <= NHL_MAX; j++) {
    const v = reg[i][j] * 0.2;
    if (i - j === 1 && i < NHL_MAX) { en[i][j] -= v; en[i + 1][j] += v; }
    if (j - i === 1 && j < NHL_MAX) { en[i][j] -= v; en[i][j + 1] += v; }
  }
  const fin = en.map((r) => r.map(() => 0));
  let tie = 0;
  for (let i = 0; i <= NHL_MAX; i++) for (let j = 0; j <= NHL_MAX; j++) {
    if (i === j) {
      tie += en[i][j];
      if (i < NHL_MAX) { fin[i + 1][j] += en[i][j] * otHome; fin[i][j + 1] += en[i][j] * (1 - otHome); }
    } else fin[i][j] += en[i][j];
  }
  return { reg: en, fin, tie };
}
// find the goal split that matches the server's win chance
function nhlSolve(p, total) {
  let lo = 0.2, hi = 0.8, g = null;
  for (let it = 0; it < 30; it++) {
    const s = (lo + hi) / 2;
    const lh = total * s, la = total * (1 - s);
    g = nhlGrids(lh, la, 0.5 + (s - 0.5) * 0.6);
    let ph = 0;
    for (let i = 0; i <= NHL_MAX; i++) for (let j = 0; j < i; j++) ph += g.fin[i][j];
    if (ph < p) lo = s; else hi = s;
    g.lh = lh; g.la = la;
  }
  return g;
}

const nhlCache = new Map();
export function nhlMarkets(game) {
  const m = game?.model;
  if (!m || !m.p || !m.total) return null;
  const key = `${game.id}|${m.p}|${m.total}`;
  if (nhlCache.has(key)) return nhlCache.get(key);
  // the model's total counts OT/shootout winners; regulation + EN is a bit lower
  const g = nhlSolve(m.p, Math.max(4, m.total * 0.97));
  const { fin, reg, tie, lh, la } = g;
  const cell = (fn) => { let v = 0; for (let i = 0; i <= NHL_MAX; i++) for (let j = 0; j <= NHL_MAX; j++) if (fn(i, j)) v += fin[i][j]; return v; };
  const homeBy2 = cell((i, j) => i - j >= 2);
  const totals = [5.5, 6.5, 7.5].map((line) => {
    const over = cell((i, j) => i + j > line);
    return { line, over: price(over), under: price(1 - over) };
  });
  const p00 = reg[0][0];
  const homeFirst = (lh / (lh + la)) * (1 - p00);
  const awayFirst = (la / (lh + la)) * (1 - p00);
  const both = cell((i, j) => i >= 1 && j >= 1);
  const shutout = cell((i, j) => i === 0 || j === 0);
  // periods: 31 / 35 / 34 % of the goals
  const periods = [0.31, 0.35, 0.34].map((f) => {
    let h = 0, d = 0, a = 0;
    for (let i = 0; i <= 8; i++) for (let j = 0; j <= 8; j++) {
      const v = poissonPmf(i, lh * f) * poissonPmf(j, la * f);
      if (i > j) h += v; else if (i === j) d += v * 1.15; else a += v;   // tied periods: more than Poisson
    }
    const n = h + d + a;
    return { home: price(h / n), draw: price(d / n), away: price(a / n) };
  });
  const out = {
    lh, la, fin, reg, cell, tie,
    puckline: { home: price(homeBy2), away: price(1 - homeBy2) },
    totals,
    teamFirst: { home: price(homeFirst), away: price(awayFirst) },
    bothScore: { yes: price(both), no: price(1 - both) },
    periods,
    overtime: price(tie),
    shutout: price(shutout),
    fight: price(0.27),   // ≈ one game in four has a fight
    exact: (h, a) => (h > NHL_MAX || a > NHL_MAX || h === a ? null : priceLong(fin[h][a], 500)),
  };
  nhlCache.set(key, out);
  return out;
}

// team totals for shots / penalty minutes (per game, from the players' stats)
export function nhlTeamTotals(game, teamRates) {
  const h = teamRates?.[game.home_abbrev], a = teamRates?.[game.away_abbrev];
  if (!h || !a) return null;
  const shots = h.shots + a.shots;
  const shotsLine = Math.floor(shots) + 0.5;
  const sd = Math.sqrt(shots) * 1.25;
  const pOverShots = 1 - normCdf((shotsLine - shots) / sd);
  const shotLines = [shotsLine - 3, shotsLine + 3].map((line) => {
    const po = 1 - normCdf((line - shots) / sd);
    return { line, over: price(po), under: price(1 - po) };
  });
  // penalty minutes: mostly 2-minute minors, streaky (fights, misconducts)
  const pim = h.pim + a.pim;
  const pmf = nbPmfArr(pim / 2, 2.2, 60);   // number of 2-minute "units"
  let best = null;
  for (let line = 4.5; line <= 40.5; line += 2) {
    let over = 0;
    pmf.forEach((v, k) => { if (k * 2 > line) over += v; });
    if (!best || Math.abs(over - 0.5) < Math.abs(best.p - 0.5)) best = { line, p: over };
  }
  return {
    shots: [{ line: shotsLine, over: price(pOverShots), under: price(1 - pOverShots) }, ...shotLines].sort((x, y) => x.line - y.line),
    pim: best ? { line: best.line, over: price(best.p), under: price(1 - best.p) } : null,
  };
}

// a skater's markets: anytime goal, first goal, winning goal, 1+ point, shots over/under
export function nhlPlayerMarkets(game, player, rates) {
  const mk = nhlMarkets(game);
  if (!mk) return null;
  const id = String(player.id ?? '').split('-').pop();
  // no stats loaded for him → not offered (a star must never get a depth player's price)
  if (!rates || !rates[id]) return null;
  const r = rates[id];
  const isHome = player.team === game.home_abbrev;
  const teamL = isHome ? mk.lh : mk.la;
  const lgTeam = 3.05;
  const f = teamL / lgTeam;               // better matchup = more goals for everyone on the team
  const g = r.g * f;
  const anytime = 1 - Math.exp(-g);
  const total = mk.lh + mk.la;
  const first = (g / total) * (1 - Math.exp(-total));
  const teamWin = isHome ? game.model.p : 1 - game.model.p;
  const winning = teamWin * (g / teamL) * 0.92;   // shootout wins have no winning-goal scorer
  const point = 1 - Math.exp(-r.pts * f);
  const sMean = Math.max(0.3, r.s * (0.85 + 0.15 * f));
  const l1 = Math.max(0.5, Math.round(sMean) - 0.5);
  const shots = [l1, l1 + 1].map((line) => {
    const pOver = poisAtLeast(Math.ceil(line), sMean);
    return { line, over: price(pOver), under: price(1 - pOver) };
  });
  return {
    anytime: price(anytime),
    first: priceLong(first),
    winning: priceLong(winning),
    point: price(point),
    shots, sMean,
  };
}

// ═════════════════════════════ MLB ═════════════════════════════
// Runs per team: negative binomial (runs come in bunches). Extra innings break ties.
const MLB_R = 2.1, MLB_MAX = 25;
const mlbCache = new Map();
export function mlbMarkets(game) {
  const m = game?.model;
  if (!m || !m.p || !m.lh) return null;
  const key = `${game.id}|${m.p}|${m.lh}|${m.la}`;
  if (mlbCache.has(key)) return mlbCache.get(key);
  const ph = nbPmfArr(m.lh, MLB_R, MLB_MAX), pa = nbPmfArr(m.la, MLB_R, MLB_MAX);
  // final score grid (ties → extra innings, winner +1)
  const fin = ph.map(() => pa.map(() => 0));
  for (let i = 0; i <= MLB_MAX; i++) for (let j = 0; j <= MLB_MAX; j++) {
    const v = ph[i] * pa[j];
    if (i === j) { if (i < MLB_MAX) { fin[i + 1][j] += v * m.p; fin[i][j + 1] += v * (1 - m.p); } }
    else fin[i][j] += v;
  }
  // total runs
  const totalMean = m.lh + m.la;
  let best = null;
  for (let line = Math.floor(totalMean) - 1.5; line <= Math.floor(totalMean) + 1.5; line += 1) {
    let over = 0;
    for (let i = 0; i <= MLB_MAX; i++) for (let j = 0; j <= MLB_MAX; j++) if (i + j > line) over += fin[i][j];
    if (!best || Math.abs(over - 0.5) < Math.abs(best.p - 0.5)) best = { line, p: over };
  }
  // run line: about 32 % of home wins are by exactly one run (walk-offs), 27 % of road wins
  const homeBy2 = m.p * 0.68;
  const teamHr = (l) => [0.5, 1.5, 2.5].map((line) => ({ line, odds: price(poisAtLeast(Math.ceil(line), l)) }));
  const out = {
    fin, cell: (fn) => { let v = 0; for (let i = 0; i <= MLB_MAX; i++) for (let j = 0; j <= MLB_MAX; j++) if (fn(i, j)) v += fin[i][j]; return v; },
    runline: { home: price(homeBy2), away: price(1 - homeBy2) },
    total: { line: best.line, over: price(best.p), under: price(1 - best.p) },
    teamHr: { home: teamHr(m.hrH || 1.15), away: teamHr(m.hrA || 1.15) },
    exact: (h, a) => (h > MLB_MAX || a > MLB_MAX || h === a ? null : priceLong(fin[h][a], 500)),
    ks: (isHome) => {
      const mean = isHome ? m.homeKs : m.awayKs;
      if (!mean) return null;
      const line = Math.round(mean) - 0.5;
      const pOver = nbAtLeast(Math.ceil(line), mean, 1.25);
      return { line, over: price(pOver), under: price(1 - pOver) };
    },
  };
  mlbCache.set(key, out);
  return out;
}

// a batter's markets, from his own per-game rates
export function mlbBatterMarkets(rates) {
  if (!rates) return null;
  const at = (k, m, r = 1.1) => price(nbAtLeast(k, m, r));
  const pAB = (k) => 1 - normCdf((k - 0.5 - rates.ab) / 0.8);
  const cycle = poisAtLeast(1, rates.s1) * poisAtLeast(1, rates.s2) * poisAtLeast(1, rates.s3) * poisAtLeast(1, rates.hr) * 0.8;
  return {
    mlb_batter_hr: { 0.5: at(1, rates.hr, 1), 1.5: at(2, rates.hr, 1), 2.5: at(3, rates.hr, 1) },
    mlb_batter_rbi: { 0.5: at(1, rates.rbi, 1.6), 1.5: at(2, rates.rbi, 1.6), 2.5: at(3, rates.rbi, 1.6), 3.5: at(4, rates.rbi, 1.6) },
    mlb_batter_sb: { 0.5: at(1, rates.sb, 1.2), 1.5: at(2, rates.sb, 1.2) },
    mlb_batter_1b: { 0.5: at(1, rates.s1) },
    mlb_batter_2b: { 0.5: at(1, rates.s2) },
    mlb_batter_3b: { 0.5: at(1, rates.s3) },
    mlb_batter_ab: { 3.5: price(pAB(4)), 4.5: price(pAB(5)) },
    mlb_batter_cycle: { null: priceLong(cycle, 5000) },
  };
}

// ═════════════════════════════ NBA ═════════════════════════════
// The game model gives the expected home margin and total. Margin ~ N(margin, 13.5), total ~ N(total, 18),
// taken as independent — so each team's points = (total ± margin) / 2, sd 11.25. Quarters and halves are
// the same model scaled down (a quarter is noisier than a quarter of a game).
const NBA_SD = 13.5;
const NBA_TOTAL_SD = 18;
const NBA_TEAM_SD = Math.sqrt(NBA_SD * NBA_SD + NBA_TOTAL_SD * NBA_TOTAL_SD) / 2;   // 11.25
const NBA_Q1_SD = 7.5, NBA_H1_SD = 9.8, NBA_H1_TOTAL_SD = 12.7, NBA_H1_SHARE = 0.495;
export const NBA_MARGIN_BANDS = ['1-5', '6-10', '11-15', '16+'];
const halfLine = (x) => Math.floor(x) + 0.5;
const normPdf = (x) => Math.exp(-x * x / 2) / Math.sqrt(2 * Math.PI);

// probability of one side of a game bet (null = not a bet we price)
function nbaGameProb(m, betType, selection, d = {}, line = null) {
  const teamMean = (side) => (side === 'home' ? (m.total + m.margin) / 2 : (m.total - m.margin) / 2);
  const over = (pOver) => (selection === 'over' ? pOver : selection === 'under' ? 1 - pOver : null);
  switch (betType) {
    case 'nba_team_total': {
      if (line == null || !['home', 'away'].includes(d.team)) return null;
      return over(1 - normCdf((line - teamMean(d.team)) / NBA_TEAM_SD));
    }
    case 'nba_quarter_winner': {     // 1st quarter (q1) or 1st half (h1); level after it = bet refunded
      const half = d.period === 'h1';
      if (!half && d.period !== 'q1') return null;
      const mean = m.margin / (half ? 2 : 4), sd = half ? NBA_H1_SD : NBA_Q1_SD;
      const pHome = 1 - normCdf((0.5 - mean) / sd), pAway = normCdf((-0.5 - mean) / sd);
      return selection === 'home' ? pHome / (pHome + pAway) : selection === 'away' ? pAway / (pHome + pAway) : null;
    }
    case 'nba_half_total':
      return line == null ? null : over(1 - normCdf((line - m.total * NBA_H1_SHARE) / NBA_H1_TOTAL_SD));
    case 'nba_margin': {             // the winner AND by how much
      if (!['home', 'away'].includes(selection) || !NBA_MARGIN_BANDS.includes(d.band)) return null;
      // P(that team wins by a number in the band), its margin ~ N(mean, 13.5)
      const band = (mean, b) => {
        if (b === '16+') return 1 - normCdf((15.5 - mean) / NBA_SD);
        const [lo, hi] = b.split('-').map(Number);
        return normCdf((hi + 0.5 - mean) / NBA_SD) - normCdf((lo - 0.5 - mean) / NBA_SD);
      };
      // the 8 outcomes (4 per team) cover everything — a game can't end level — so they add up to 1
      const all = NBA_MARGIN_BANDS.reduce((t, b) => t + band(m.margin, b) + band(-m.margin, b), 0);
      return band(selection === 'home' ? m.margin : -m.margin, d.band) / all;
    }
    case 'nba_overtime': {           // level after 4 quarters: about 6 % of even games, less when one team is a big favourite
      const p = clamp(2.1 * normPdf(m.margin / NBA_SD) / NBA_SD, 0.02, 0.09);
      return selection === 'yes' ? p : selection === 'no' ? 1 - p : null;
    }
    case 'nba_odd_even':
      return selection === 'odd' || selection === 'even' ? 0.5 : null;
    case 'nba_both_110': {           // both teams reach 110: total ≥ 220 + |margin|
      let p = 0;
      for (let x = -60; x <= 60; x += 0.5) {
        p += normPdf((x - m.margin) / NBA_SD) / NBA_SD * 0.5 * (1 - normCdf((220 + Math.abs(x) - m.total) / NBA_TOTAL_SD));
      }
      return selection === 'yes' ? p : selection === 'no' ? 1 - p : null;
    }
    default: return null;
  }
}
// odds for a game bet. Long shots (a margin band, overtime: yes) are priced like the other long shots.
// A side that is almost certain (≥ 97 %) isn't offered: at 1.01 it would pay more than it should.
function nbaGamePrice(m, betType, selection, d, line) {
  const p = nbaGameProb(m, betType, selection, d, line);
  if (p == null || !(p > 0) || p > 0.97) return null;
  const long = betType === 'nba_margin' || (betType === 'nba_overtime' && selection === 'yes');
  return long ? priceLong(p, 100) : price(p);
}

export function nbaMarkets(game) {
  const m = game?.model;
  if (!m || m.margin == null || !m.total) return null;
  const line = Math.floor(m.margin) + 0.5;        // home must win by more than this
  const pHome = 1 - normCdf((line - m.margin) / NBA_SD);
  const tLine = Math.floor(m.total) + 0.5;
  const pOver = 1 - normCdf((tLine - m.total) / NBA_TOTAL_SD);
  const q = (bt, sel, d, l) => nbaGamePrice(m, bt, sel, d, l);
  const teamTotal = (team) => {
    const l = halfLine(team === 'home' ? (m.total + m.margin) / 2 : (m.total - m.margin) / 2);
    return { line: l, over: q('nba_team_total', 'over', { team }, l), under: q('nba_team_total', 'under', { team }, l) };
  };
  const hLine = halfLine(m.total * NBA_H1_SHARE);
  const winner = (period) => ({ home: q('nba_quarter_winner', 'home', { period }), away: q('nba_quarter_winner', 'away', { period }) });
  const yn = (bt) => ({ yes: q(bt, 'yes'), no: q(bt, 'no') });
  return {
    spread: { line, home: price(pHome), away: price(1 - pHome) },
    total: { line: tLine, over: price(pOver), under: price(1 - pOver) },
    teamTotal: { home: teamTotal('home'), away: teamTotal('away') },
    q1: winner('q1'),
    h1: winner('h1'),
    halfTotal: { line: hLine, over: q('nba_half_total', 'over', {}, hLine), under: q('nba_half_total', 'under', {}, hLine) },
    margin: {
      home: Object.fromEntries(NBA_MARGIN_BANDS.map((band) => [band, q('nba_margin', 'home', { band })])),
      away: Object.fromEntries(NBA_MARGIN_BANDS.map((band) => [band, q('nba_margin', 'away', { band })])),
    },
    overtime: yn('nba_overtime'),
    oddEven: { odd: q('nba_odd_even', 'odd'), even: q('nba_odd_even', 'even') },
    both110: yn('nba_both_110'),
  };
}

// ── players ──
const NBA_STAT = { nba_player_pts: 'pts', nba_player_reb: 'reb', nba_player_ast: 'ast', nba_player_3pm: 'tpm', nba_player_stl: 'stl', nba_player_blk: 'blk' };
const nbaSpread = { reb: 1.4, ast: 1.4, tpm: 1.3, stl: 1.2, blk: 1.3 };     // how streaky each count is
const ptsSd = (mean) => 0.28 * mean + 1.5;
// P(over the line) for one stat
function nbaStatOver(rates, key, line) {
  const mean = rates?.[key];
  if (!(mean > 0) || line == null) return null;
  if (key === 'pts') return 1 - normCdf((line - mean) / ptsSd(mean));
  return nbAtLeast(Math.floor(line) + 1, mean, nbaSpread[key] || 1.4);
}
const praMean = (r) => (r.pts || 0) + (r.reb || 0) + (r.ast || 0);
const praOver = (r, line) => { const mean = praMean(r); return mean > 0 ? 1 - normCdf((line - mean) / (0.25 * mean + 2)) : null; };
// offered only when the price means something (a 99 % "no" at 1.01 pays too much)
const safe = (p, long = false) => (p == null || !(p > 0.004) || p > 0.97 ? null : long ? priceLong(p, 200) : price(p));

// a player's lines: set at his own average, priced from how often he goes over / stays under
export function nbaPlayerMarkets(rates) {
  if (!rates || !(rates.min > 0)) return null;
  const mk = (key) => {
    const mean = rates[key];
    if (!(mean > 0.3)) return null;
    const line = Math.max(0.5, Math.round(mean) - 0.5);
    const p = nbaStatOver(rates, key, line);
    return { line, odds: safe(p), under: safe(1 - p) };
  };
  const praLine = Math.max(0.5, Math.round(praMean(rates)) - 0.5);
  const pPra = praMean(rates) > 3 ? praOver(rates, praLine) : null;
  // double-double / triple-double: how often he really gets one (his own count per game played)
  const dd = rates.dd > 0 ? clamp(rates.dd, 0, 0.95) : null;
  const td = rates.td > 0 ? clamp(rates.td, 0, 0.9) : null;
  return {
    pts: mk('pts'), reb: mk('reb'), ast: mk('ast'), tpm: mk('tpm'), stl: mk('stl'), blk: mk('blk'),
    pra: pPra == null ? null : { line: praLine, odds: safe(pPra), under: safe(1 - pPra) },
    dd: dd == null ? null : { yes: safe(dd, dd < 0.25), no: safe(1 - dd) },
    td: td == null ? null : { yes: safe(td, true), no: safe(1 - td) },
  };
}

// Top scorer of the game / first basket: one price per player of the field (the players of both teams
// that the market lists). field = [{ id, rates }]
export function nbaTopScorerProbs(field) {
  const ps = field.map(({ rates }) => ({ mean: rates?.pts || 0, sd: ptsSd(rates?.pts || 0) }));
  const out = ps.map(() => 0);
  for (let x = 0.25; x <= 75; x += 0.5) {             // P(he scores x and everybody else scores less)
    const cdfs = ps.map((p) => (p.mean > 0 ? normCdf((x - p.mean) / p.sd) : 1));
    ps.forEach((p, i) => {
      if (!(p.mean > 0)) return;
      let others = 1;
      cdfs.forEach((c, k) => { if (k !== i) others *= c; });
      out[i] += normPdf((x - p.mean) / p.sd) / p.sd * 0.5 * others;
    });
  }
  const sum = out.reduce((a, b) => a + b, 0) || 1;
  return out.map((v) => v / sum);
}
export function nbaFirstBasketProbs(field) {
  // the first basket goes to a starter who shoots a lot: points × (minutes / 36)², starters weigh most
  const w = field.map(({ rates }) => (rates?.pts > 0 && rates?.min > 0 ? rates.pts * Math.pow(Math.min(rates.min, 38) / 36, 2) * (rates.min >= 24 ? 1 : 0.35) : 0));
  const sum = w.reduce((a, b) => a + b, 0) || 1;
  return w.map((v) => v / sum);
}
// { [playerId]: odds } for the two "pick one player" markets
export function nbaFieldMarkets(field) {
  const top = nbaTopScorerProbs(field), first = nbaFirstBasketProbs(field);
  return {
    topScorer: Object.fromEntries(field.map((x, i) => [String(x.id), safe(top[i], true)])),
    firstBasket: Object.fromEntries(field.map((x, i) => [String(x.id), safe(first[i], true)])),
  };
}

// ═════════════════ the one checkpoint: price ANY bet from the models ═════════════════
// Every bet going into the slip is priced here, whatever button it came from
// (game card, scout report, strategy…). Returns the odds, or null = can't be priced → not offered.
// ctx: { nhlRates, nhlTeams, nbaRates, mlbRates, findNhlPlayer(id, name) }
export function quoteBet(game, betType, selection, details = {}, ctx = {}) {
  if (String(game?.id).startsWith('f1-') || String(betType).startsWith('f1_')) return quoteF1(game, betType, selection, details);
  if (String(game?.id).startsWith('nfl-')) return betType === 'winner' ? (parseFloat(selection === 'home' ? game.home_odds : game.away_odds) > 1 ? parseFloat(selection === 'home' ? game.home_odds : game.away_odds) : null) : quoteNFL(game, betType, selection, details, ctx);
  if (!game?.model) return null;
  const sport = game.sport || (String(game.id).startsWith('mlb-') ? 'mlb' : String(game.id).startsWith('nba-') ? 'nba' : 'nhl');
  const d = details || {};
  const line = d.line != null && d.line !== '' ? parseFloat(d.line) : null;
  const ou = (pOver) => (selection === 'over' ? price(pOver) : selection === 'under' ? price(1 - pOver) : null);
  const yesNo = (pYes) => (selection === 'no' ? price(1 - pYes) : price(pYes));

  if (betType === 'winner') {
    const o = parseFloat(selection === 'home' ? game.home_odds : game.away_odds);
    return o > 1 ? o : null;
  }

  if (sport === 'nhl') {
    const mk = nhlMarkets(game);
    if (!mk) return null;
    const margin = (i, j) => (selection === 'home' ? i - j : j - i);
    switch (betType) {
      case 'puckline': {
        const l = line ?? (selection === 'home' ? -1.5 : 1.5);
        return price(mk.cell((i, j) => margin(i, j) + l > 0));
      }
      case 'total': return line == null ? null : ou(mk.cell((i, j) => i + j > line));
      case 'team_first': return selection === 'home' ? mk.teamFirst.home : mk.teamFirst.away;
      case 'both_score': return yesNo(mk.cell((i, j) => i >= 1 && j >= 1));
      case 'period_winner': {
        const idx = { '1st': 0, '2nd': 1, '3rd': 2 }[d.period] ?? 0;
        return mk.periods[idx][selection] ?? null;
      }
      case 'overtime': return yesNo(mk.tie);
      case 'shutout': return yesNo(mk.cell((i, j) => i === 0 || j === 0));
      case 'fighting': return yesNo(0.27);
      case 'exact_score': return mk.exact(parseInt(d.home), parseInt(d.away));
      case 'total_shots':
      case 'penalty_mins': {
        const h = ctx.nhlTeams?.[game.home_abbrev], a = ctx.nhlTeams?.[game.away_abbrev];
        if (!h || !a || line == null) return null;
        if (betType === 'total_shots') {
          const mean = h.shots + a.shots;
          return ou(1 - normCdf((line - mean) / (Math.sqrt(mean) * 1.25)));
        }
        const pmf = nbPmfArr((h.pim + a.pim) / 2, 2.2, 80);
        let over = 0;
        pmf.forEach((v, k) => { if (k * 2 > line) over += v; });
        return ou(over);
      }
      case 'first_goal':
      case 'anytime_goal':
      case 'winning_goal':
      case 'player_points':
      case 'player_shots': {
        const player = ctx.findNhlPlayer ? ctx.findNhlPlayer(d.playerId, d.playerName) : null;
        if (!player || !player.team) return null;   // unknown player → not offered
        if (player.team !== game.home_abbrev && player.team !== game.away_abbrev) return null;
        const pm = nhlPlayerMarkets(game, player, ctx.nhlRates);
        if (!pm) return null;
        if (betType === 'first_goal') return pm.first;
        if (betType === 'anytime_goal') return pm.anytime;
        if (betType === 'winning_goal') return pm.winning;
        if (betType === 'player_points') return pm.point;
        if (line == null) return null;
        return ou(poisAtLeast(Math.floor(line) + 1, pm.sMean));
      }
      default: return null;
    }
  }

  if (sport === 'mlb') {
    const mk = mlbMarkets(game);
    if (!mk) return null;
    const m = game.model;
    switch (betType) {
      case 'runline': {
        const l = line ?? (selection === 'home' ? -1.5 : 1.5);
        const homeBy2 = m.p * 0.68, awayBy2 = (1 - m.p) * 0.73;
        let p = null;
        if (selection === 'home' && l === -1.5) p = homeBy2;
        if (selection === 'away' && l === 1.5) p = 1 - homeBy2;
        if (selection === 'away' && l === -1.5) p = awayBy2;
        if (selection === 'home' && l === 1.5) p = 1 - awayBy2;
        return p == null ? null : price(p);
      }
      case 'total': return line == null ? null : ou(mk.cell((i, j) => i + j > line));
      case 'exact_score': return mk.exact(parseInt(d.home), parseInt(d.away));
      case 'mlb_team_hr': {
        const l = selection === 'home' ? (m.hrH || 1.15) : (m.hrA || 1.15);
        return line == null ? null : price(poisAtLeast(Math.floor(line) + 1, l));
      }
      case 'mlb_pitcher_ks': {
        const isHome = String(d.playerId) === String(game.home_pitcher_id);
        const isAway = String(d.playerId) === String(game.away_pitcher_id);
        const mean = isHome ? m.homeKs : isAway ? m.awayKs : null;
        if (!mean || line == null) return null;   // only today's probable starters
        return ou(nbAtLeast(Math.floor(line) + 1, mean, 1.25));
      }
      default: {
        if (!betType.startsWith('mlb_batter_')) return null;
        const bm = mlbBatterMarkets(ctx.mlbRates?.[String(d.playerId)]);
        if (!bm || !bm[betType]) return null;
        return bm[betType][String(line)] ?? null;
      }
    }
  }

  if (sport === 'nba') {
    const m = game.model;
    switch (betType) {
      case 'spread': {
        if (line == null) return null;
        // home line −x: home covers when it wins by more than x; away line +x: away covers otherwise
        const need = selection === 'home' ? -line : line;
        const pHome = 1 - normCdf((need - m.margin) / NBA_SD);
        return price(selection === 'home' ? pHome : 1 - pHome);
      }
      case 'total': return line == null ? null : ou(1 - normCdf((line - m.total) / NBA_TOTAL_SD));
      case 'nba_team_total':
      case 'nba_quarter_winner':
      case 'nba_half_total':
      case 'nba_margin':
      case 'nba_overtime':
      case 'nba_odd_even':
      case 'nba_both_110':
        return nbaGamePrice(m, betType, selection, d, line);
      case 'nba_player_pts':
      case 'nba_player_reb':
      case 'nba_player_ast':
      case 'nba_player_3pm':
      case 'nba_player_stl':
      case 'nba_player_blk': {
        const r = ctx.nbaRates?.[String(d.playerId)];
        if (!r || line == null) return null;
        const pOver = nbaStatOver(r, NBA_STAT[betType], line);
        return pOver == null ? null : safe(selection === 'over' ? pOver : selection === 'under' ? 1 - pOver : null);
      }
      case 'nba_player_pra': {
        const r = ctx.nbaRates?.[String(d.playerId)];
        if (!r || line == null) return null;
        const pOver = praOver(r, line);
        return pOver == null ? null : safe(selection === 'over' ? pOver : selection === 'under' ? 1 - pOver : null);
      }
      case 'nba_double_double':
      case 'nba_triple_double': {
        const mk = nbaPlayerMarkets(ctx.nbaRates?.[String(d.playerId)]);
        const x = mk?.[betType === 'nba_double_double' ? 'dd' : 'td'];
        return x ? (selection === 'no' ? x.no : x.yes) : null;
      }
      case 'nba_top_scorer':
      case 'nba_first_basket': {
        // the field = the players the market listed (both teams); a player we have no numbers for can't be in it
        const ids = Array.isArray(d.field) ? d.field.map(String).filter((id) => ctx.nbaRates?.[id]) : [];
        if (ids.length < 6 || ids.length > 30 || !ids.includes(String(d.playerId))) return null;
        const mk = nbaFieldMarkets(ids.map((id) => ({ id, rates: ctx.nbaRates[id] })));
        return mk[betType === 'nba_top_scorer' ? 'topScorer' : 'firstBasket'][String(d.playerId)] ?? null;
      }
      default: return null;
    }
  }
  return null;
}

// ═════════════════════════════ F1 ═════════════════════════════
// The server simulates the race 20,000 times; these are the shares of simulations.
// Top 3 / 6 / 10, head-to-head, winning team: 5 %. Winner / pole / fastest lap are one-in-22
// picks the model is less sure about: priced like the other long shots.
export function f1Markets(race) {
  const m = race?.model;
  if (!m?.drivers?.length) return null;
  const poleOpen = !race.quali_time || Date.now() < new Date(race.quali_time).getTime();
  const drivers = m.drivers.map((d) => ({
    ...d,
    odds: {
      f1_winner: priceLong(d.win, 500),
      f1_top3: price(d.top3, 500),
      f1_top6: price(d.top6, 500),
      f1_top10: price(d.top10, 500),
      f1_pole: poleOpen ? priceLong(d.pole, 500) : null,
      f1_fastest_lap: priceLong(d.fl, 500),
    },
  }));
  const h2h = m.h2h.map((x) => ({ ...x, oddsA: price(x.pa), oddsB: price(1 - x.pa) }));
  const constructors = m.constructors.map((c) => ({ ...c, odds: price(c.win, 500) }));
  return { drivers, h2h, constructors, poleOpen };
}

export function quoteF1(race, betType, selection, details = {}) {
  if (!race?.bettable || race.status !== 'FUT') return null;
  const mk = f1Markets(race);
  if (!mk) return null;
  if (betType === 'f1_h2h') {
    const x = mk.h2h.find((h) => (h.a === selection && h.b === details.vs) || (h.b === selection && h.a === details.vs));
    if (!x) return null;
    return x.a === selection ? x.oddsA : x.oddsB;
  }
  if (betType === 'f1_constructor') return mk.constructors.find((c) => c.team === selection)?.odds ?? null;
  const d = mk.drivers.find((x) => x.code === selection);
  return d?.odds[betType] ?? null;
}

// ═════════════════════════════ NFL ═════════════════════════════
// Final margin: a bell curve around the expected margin, with extra weight on the numbers
// games really end on (3, 7, 10, 6, 4, 14…) — so a +3.5 / −2.5 is priced right.
const NFL_SD = 13.5;
const NFL_KEYS = { 3: 2.3, 7: 1.6, 10: 1.3, 6: 1.2, 4: 1.15, 14: 1.2, 1: 0.8, 2: 0.8, 5: 0.9, 0: 0.08 };
const nflMarginCache = new Map();
function nflMarginPmf(mu) {
  const key = Math.round(mu * 100);
  if (nflMarginCache.has(key)) return nflMarginCache.get(key);
  const pmf = {};
  let sum = 0;
  for (let k = -70; k <= 70; k++) {
    const w = Math.exp(-((k - mu) ** 2) / (2 * NFL_SD * NFL_SD)) * (NFL_KEYS[Math.abs(k)] ?? 1);
    pmf[k] = w; sum += w;
  }
  for (const k in pmf) pmf[k] /= sum;
  nflMarginCache.set(key, pmf);
  return pmf;
}
// P(home margin > x)
const nflHomeBy = (mu, x) => { const pmf = nflMarginPmf(mu); let p = 0; for (const k in pmf) if (Number(k) > x) p += pmf[k]; return p; };

export function nflMarkets(game) {
  const m = game?.model;
  if (!m || m.margin == null || !m.total) return null;
  const line = Math.floor(m.margin) + 0.5;   // home must win by more than this
  const pHome = nflHomeBy(m.margin, line);
  const tLine = Math.floor(m.total) + 0.5;
  const pOver = 1 - normCdf((tLine - m.total) / 13);
  // 1st half: half the margin, ties at the half ≈ 11 %
  const TIE_H = 0.11;
  const hHome = (1 - TIE_H) * normCdf((m.margin / 2) / 9.8);
  // both teams 20+: each team's points ~ bell curve (sd 9.5), a little positive link (shootouts)
  const p20 = (pts) => 1 - normCdf((19.5 - pts) / 9.5);
  const ph = p20(m.ph ?? m.total / 2), pa = p20(m.pa ?? m.total / 2);
  const both = Math.min(0.99, ph * pa + 0.1 * Math.sqrt(ph * (1 - ph) * pa * (1 - pa)));
  // overtime ≈ 5.5 % in a pick'em, less when one team is favoured
  const ot = 0.055 * Math.exp(-((m.margin / NFL_SD) ** 2) / 2);
  return {
    spread: { line, home: price(pHome), away: price(1 - pHome) },
    total: { line: tLine, over: price(pOver), under: price(1 - pOver) },
    half: { home: price(hHome), tie: price(TIE_H), away: price(1 - TIE_H - hHome) },
    both20: { yes: price(both), no: price(1 - both) },
    overtime: priceLong(ot),
  };
}

// yards: skewed (a few big games), so a log-normal curve; the line sits at the player's typical game
const YDS_CV = { pass: 0.3, rush: 0.55, rec: 0.65 };
const logNormOver = (x, mean, cv) => {
  const s2 = Math.log(1 + cv * cv), mu = Math.log(mean) - s2 / 2;
  return 1 - normCdf((Math.log(Math.max(x, 0.01)) - mu) / Math.sqrt(s2));
};
function nflYards(mean, kind) {
  if (!(mean > 5)) return null;
  const cv = YDS_CV[kind];
  const median = mean / Math.sqrt(1 + cv * cv);
  const line = Math.round(median) + 0.5;
  const pOver = logNormOver(line, mean, cv);
  return { line, over: price(pOver), under: price(1 - pOver), mean, cv };
}
// a player's markets; `game` gives the team's expected points (better matchup = more for everyone)
export function nflPlayerMarkets(game, player) {
  const m = game?.model, r = player?.rates;
  if (!m || !r) return null;
  const teamPts = player.team === game.home_abbrev ? (m.ph ?? m.total / 2) : (m.pa ?? m.total / 2);
  const f = teamPts / 22.5;
  const tdRate = (r.td || 0) * f;
  const gameTds = Math.max(2, m.total * 0.105);           // ≈ 4.6 touchdowns in a 44-point game
  const anytime = 1 - Math.exp(-tdRate);
  const first = (tdRate / gameTds) * (1 - Math.exp(-gameTds));
  const scale = 0.7 + 0.3 * f;
  const pos = player.pos;
  return {
    anytime: pos === 'QB' && r.td < 0.15 ? null : price(anytime),
    first: pos === 'QB' && r.td < 0.15 ? null : priceLong(first),
    pass: pos === 'QB' ? nflYards(r.pass * scale, 'pass') : null,
    rush: r.rush >= 15 ? nflYards(r.rush * scale, 'rush') : null,
    rec: pos !== 'QB' && r.rec >= 10 ? nflYards(r.rec * scale, 'rec') : null,
  };
}

function quoteNFL(game, betType, selection, details = {}, ctx = {}) {
  const mk = nflMarkets(game);
  if (!mk) return null;
  const m = game.model;
  const line = details.line != null && details.line !== '' ? parseFloat(details.line) : null;
  switch (betType) {
    case 'spread': {
      if (line == null) return null;
      const need = selection === 'home' ? -line : line;   // home −x: wins by more than x
      const pHome = nflHomeBy(m.margin, need);
      return price(selection === 'home' ? pHome : 1 - pHome);
    }
    case 'total': {
      if (line == null) return null;
      const pOver = 1 - normCdf((line - m.total) / 13);
      return selection === 'over' ? price(pOver) : selection === 'under' ? price(1 - pOver) : null;
    }
    case 'nfl_half_winner': return mk.half[selection] ?? null;
    case 'nfl_both_20': return selection === 'no' ? mk.both20.no : mk.both20.yes;
    case 'nfl_overtime': return selection === 'yes' ? mk.overtime : null;
    default: {
      const player = ctx.findNflPlayer ? ctx.findNflPlayer(game.id, details.playerId) : null;
      const pm = player ? nflPlayerMarkets(game, player) : null;
      if (!pm) return null;
      if (betType === 'nfl_anytime_td') return pm.anytime;
      if (betType === 'nfl_first_td') return pm.first;
      const kind = { nfl_pass_yds: 'pass', nfl_rush_yds: 'rush', nfl_rec_yds: 'rec' }[betType];
      const y = kind && pm[kind];
      if (!y || line == null) return null;
      const pOver = logNormOver(line, y.mean, y.cv);
      return selection === 'over' ? price(pOver) : selection === 'under' ? price(1 - pOver) : null;
    }
  }
}
